import axios from 'axios'

interface FontData {
  family: string
  name: string
  url: string
  id?: string
}

interface FontResponse {
  id: string
  family: string
  name: string
  url: string
}

export const AVAILABLE_FONTS: FontData[] = [
  // Komika
  {
    family: 'Komika',
    name: 'Komika Title',
    url: '/fonts/KOMTIT__.ttf',
  },
  {
    family: 'Komika',
    name: 'Komika Title Axis',
    url: '/fonts/KOMTITA_.ttf',
  },
  {
    family: 'Komika',
    name: 'Komika Title Tilt',
    url: '/fonts/KOMTITTL.ttf',
  },

  // Montserrat
  {
    family: 'Montserrat',
    name: 'Montserrat Thin',
    url: '/fonts/Montserrat-Thin.ttf',
  },
  {
    family: 'Montserrat',
    name: 'Montserrat ExtraLight',
    url: '/fonts/Montserrat-ExtraLight.ttf',
  },
  {
    family: 'Montserrat',
    name: 'Montserrat Light',
    url: '/fonts/Montserrat-Light.ttf',
  },
  {
    family: 'Montserrat',
    name: 'Montserrat Regular',
    url: '/fonts/Montserrat-Regular.ttf',
  },
  {
    family: 'Montserrat',
    name: 'Montserrat Medium',
    url: '/fonts/Montserrat-Medium.ttf',
  },
  {
    family: 'Montserrat',
    name: 'Montserrat SemiBold',
    url: '/fonts/Montserrat-SemiBold.ttf',
  },
  {
    family: 'Montserrat',
    name: 'Montserrat Bold',
    url: '/fonts/Montserrat-Bold.ttf',
  },
  {
    family: 'Montserrat',
    name: 'Montserrat ExtraBold',
    url: '/fonts/Montserrat-ExtraBold.ttf',
  },
  {
    family: 'Montserrat',
    name: 'Montserrat Black',
    url: '/fonts/Montserrat-Black.ttf',
  },

  // Poppins
  {
    family: 'Poppins',
    url: '/fonts/Poppins-Thin.ttf',
    name: 'Poppins Thin',
  },
  {
    family: 'Poppins',
    url: '/fonts/Poppins-ExtraLight.ttf',
    name: 'Poppins ExtraLight',
  },
  {
    family: 'Poppins',
    url: '/fonts/Poppins-Light.ttf',
    name: 'Poppins Light',
  },
  {
    family: 'Poppins',
    url: '/fonts/Poppins-Regular.ttf',
    name: 'Poppins Regular',
  },
  {
    family: 'Poppins',
    url: '/fonts/Poppins-Medium.ttf',
    name: 'Poppins Medium',
  },
  {
    family: 'Poppins',
    url: '/fonts/Poppins-SemiBold.ttf',
    name: 'Poppins SemiBold',
  },
  {
    family: 'Poppins',
    name: 'Poppins Bold',
    url: '/fonts/Poppins-Bold.ttf',
  },
  {
    family: 'Poppins',
    name: 'Poppins ExtraBold',
    url: '/fonts/Poppins-ExtraBold.ttf',
  },
  {
    family: 'Poppins',
    name: 'Poppins Black',
    url: '/fonts/Poppins-Black.ttf',
  },
];

const editorApi = axios.create({
  baseURL: 'https://api.autoeditor.video',
})

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
})

// These families are provided by index.html's stylesheet, not uploaded-font IDs.
const CSS_FONT_FAMILIES = new Set(['Roboto', 'Bangers']);
const FONT_MISS_COOLDOWN_MS = 60_000;
const MAX_CACHED_FONTS = 128;
const fontRequests = new Map<string, { promise: Promise<FontResponse | null>; expiresAt: number }>();

const fetchFontData = async (fontId: string): Promise<FontResponse | null> => {
  const foundFromConstants = AVAILABLE_FONTS.find((f) => f.name === fontId)
  if (foundFromConstants) {
    return {
      ...foundFromConstants,
      id: foundFromConstants.name,
      url: `https://autoeditor.video${foundFromConstants.url}`,
    }
  }

  try {
    const response = await api.request({
      url: `/api/fonts/${encodeURIComponent(fontId)}`,
      timeout: 5_000,
    })
    return response.data
  } catch {
    try {
      const response = await editorApi.request({
        url: `/api/fonts/${encodeURIComponent(fontId)}`,
        timeout: 5_000,
      })

      return response.data
    } catch {
      return null
    }
  }
}

/** Share in-flight requests across subtitles, layers and remounted previews.
 * Failed lookups expire so a transient outage can recover without a reload. */
const getFontData = (fontId: string): Promise<FontResponse | null> => {
  if (!fontId || CSS_FONT_FAMILIES.has(fontId)) return Promise.resolve(null);
  const cached = fontRequests.get(fontId);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;
  const entry = { promise: Promise.resolve<FontResponse | null>(null), expiresAt: Infinity };
  entry.promise = fetchFontData(fontId).then(font => {
    entry.expiresAt = font ? Infinity : Date.now() + FONT_MISS_COOLDOWN_MS;
    return font;
  });
  fontRequests.delete(fontId);
  fontRequests.set(fontId, entry);
  if (fontRequests.size > MAX_CACHED_FONTS) {
    fontRequests.delete(fontRequests.keys().next().value!);
  }
  return entry.promise;
};

export default getFontData
